import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateRangePicker, { DATE_PRESETS } from "../../common/DateRangePicker";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
  BdcBillingFile,
  cancelBdcBilling,
  createBdcBilling,
  downloadBdcBilling,
  getBdcBilling,
  listBdcBillings,
  previewBdcBilling,
} from "../../../services/bdcBilling";
import {
  BdcBillingDetail,
  BdcBillingPreview,
  BdcBillingStatus,
  BdcBillingSummary,
} from "../../../types/bdc.type";
import { BdcApiError, readBdcError } from "../apiError";
import { BDC_BILLING_PREVIEW_ROW_CAP } from "../constants";
import { baht, pcs, thaiDate, thaiDateTime, todayBkk } from "../format";
import ReasonDialog from "../ReasonDialog";
import { CARD_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { useBlobExport } from "../useBlobExport";
import {
  billingRangeProblem,
  countChecks,
  defaultDueDate,
  dueDateProblem,
  previousMonthRange,
  thaiMonth,
} from "./billing";
import BillingCheckList from "./CheckList";
import BillingRowsTable from "./RowsTable";

const ALL = "all";
const LIST_LIMIT = 20;

const FILES: { file: BdcBillingFile; label: string; name: (no: string) => string }[] = [
  { file: "package", label: "zip ทั้งชุด", name: (no) => `${no}.zip` },
  { file: "xlsx", label: "Excel", name: (no) => `IFS ${no}.xlsx` },
  { file: "pdf", label: "PDF", name: (no) => `${no}.pdf` },
];

function Stat({ label, value, strong }: Readonly<{ label: string; value: string; strong?: boolean }>) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: strong ? 700 : 500, fontSize: strong ? "1.15rem" : "1rem" }} className="mono">
        {value}
      </Typography>
    </Box>
  );
}

/** zip / Excel / PDF of one ใบวางบิล. */
function DownloadButtons({
  billing,
  busy,
  onDownload,
}: Readonly<{
  billing: Pick<BdcBillingSummary, "uuid" | "billing_no">;
  busy: string | null;
  onDownload: (billing: Pick<BdcBillingSummary, "uuid" | "billing_no">, file: BdcBillingFile, name: string) => void;
}>) {
  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
      {FILES.map(({ file, label, name }) => (
        <Button
          key={file}
          size="small"
          variant={file === "package" ? "contained" : "outlined"}
          loading={busy === `${billing.uuid}:${file}`}
          onClick={() => onDownload(billing, file, name(billing.billing_no))}
        >
          {label}
        </Button>
      ))}
    </Stack>
  );
}

/** A bill covers one calendar month, so only the whole-month shortcuts. */
const BILLING_PRESET_GROUPS = [[DATE_PRESETS.lastMonth, DATE_PRESETS.thisMonth]];

/**
 * BDC · ใบวางบิล — one a month to CP All head office, BK and RY together (head-office accounting
 * + MASTER). Pick the INV date range (default: last month) → ตรวจ: totals, the checks (errors stop
 * the issue: DC without รหัสลูกค้า, a branch missing its defaults, no documents …; warnings: gaps in
 * the INV numbers, days without INV, INV / CN left from before …) and the rows as the IFS file will
 * list them → document date and due date (from the check) → ออกใบวางบิล → zip (IFS xlsx + PDF + the
 * POs' ORDS / PICKLST), Excel or PDF. The list below re-downloads and cancels (frees the documents).
 */
export default function BdcBilling() {
  const today = todayBkk();
  const [range, setRange] = useState(() => previousMonthRange(today));
  const [preview, setPreview] = useState<BdcBillingPreview | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [docDate, setDocDate] = useState(today);
  const [dueDate, setDueDate] = useState(defaultDueDate(today));
  const [busy, setBusy] = useState<"preview" | "create" | null>(null);
  const [failure, setFailure] = useState<BdcApiError | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [issued, setIssued] = useState<BdcBillingDetail | null>(null);

  const [year, setYear] = useState<number>(Number(today.slice(0, 4)));
  const [status, setStatus] = useState<string>(ALL);
  const [page, setPage] = useState(0);
  const [list, setList] = useState<BdcBillingSummary[]>([]);
  const [listCount, setListCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [viewing, setViewing] = useState<BdcBillingDetail | null>(null);
  const [viewLoading, setViewLoading] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<BdcBillingSummary | null>(null);
  const { busy: downloading, run } = useBlobExport();

  const rangeKey = `${range.from}|${range.to}`;
  const rangeProblem = billingRangeProblem(range.from, range.to);
  const current = preview && previewKey === rangeKey ? preview : null;
  const checkCount = countChecks(current?.checks ?? []);
  const dateProblem = dueDateProblem(docDate, dueDate);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcBillings({
        page: page + 1,
        limit: LIST_LIMIT,
        year,
        ...(status !== ALL ? { status: status as BdcBillingStatus } : {}),
      });
      setList(data?.rows ?? []);
      setListCount(data?.count ?? 0);
    } catch {
      setList([]);
      setListCount(0);
    } finally {
      setListLoading(false);
    }
  }, [page, year, status]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const pickRange = (from: Dayjs, to: Dayjs) => {
    setRange({ from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") });
  };

  const runPreview = async () => {
    if (rangeProblem) return;
    setBusy("preview");
    setFailure(null);
    setIssued(null);
    try {
      const {
        data: { data },
      } = await previewBdcBilling(range.from, range.to);
      setPreview(data);
      setPreviewKey(rangeKey);
      if (data) {
        const doc = data.suggested_doc_date || today;
        setDocDate(doc);
        setDueDate(data.suggested_due_date || defaultDueDate(doc));
      }
    } catch (e) {
      setPreview(null);
      setFailure(readBdcError(e, "ตรวจใบวางบิลไม่สำเร็จ"));
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    setBusy("create");
    setFailure(null);
    try {
      const {
        data: { data },
      } = await createBdcBilling({ date_from: range.from, date_to: range.to, doc_date: docDate, due_date: dueDate });
      setConfirm(false);
      setPreview(null);
      setPreviewKey(null);
      setIssued(data);
      setPage(0);
      loadList();
    } catch (e) {
      setConfirm(false);
      setFailure(readBdcError(e, "ออกใบวางบิลไม่สำเร็จ"));
    } finally {
      setBusy(null);
    }
  };

  const download = (billing: Pick<BdcBillingSummary, "uuid" | "billing_no">, file: BdcBillingFile, name: string) =>
    run(`${billing.uuid}:${file}`, () => downloadBdcBilling(billing.uuid, file), name);

  const openDetail = async (billing: BdcBillingSummary) => {
    setViewLoading(billing.uuid);
    try {
      const {
        data: { data },
      } = await getBdcBilling(billing.uuid);
      setViewing(data);
    } catch {
      // reported by the interceptor
    } finally {
      setViewLoading(null);
    }
  };

  const cancel = async (reason: string) => {
    if (!cancelling) return false;
    try {
      await cancelBdcBilling(cancelling.uuid, reason);
      if (issued?.uuid === cancelling.uuid) setIssued(null);
      // the freed documents change what a check of that month finds
      setPreview(null);
      setPreviewKey(null);
      loadList();
      return true;
    } catch {
      return false;
    }
  };

  const thisYear = Number(today.slice(0, 4));
  const years = [thisYear, thisYear - 1, thisYear - 2];
  const truncated = current ? current.rows.length >= BDC_BILLING_PREVIEW_ROW_CAP : false;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          BDC · ใบวางบิล
        </Typography>
        <Typography variant="body2" color="text.secondary">
          เดือนละใบถึง CP All สำนักงานใหญ่ รวม BK + RY · INV จริงที่ออกแล้วในช่วง + CN (ยอดติดลบ อ้างอิง PO) ·
          ไฟล์ส่งให้บัญชี email เอง
        </Typography>
      </Box>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography fontWeight={600}>ออกใบวางบิล</Typography>
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
            <DateRangePicker
              dateFrom={dayjs(range.from)}
              dateTo={dayjs(range.to)}
              presetGroups={BILLING_PRESET_GROUPS}
              onChange={pickRange}
            />
            <Button
              variant="contained"
              startIcon={<FactCheckIcon />}
              disabled={Boolean(rangeProblem)}
              loading={busy === "preview"}
              onClick={runPreview}
            >
              ตรวจก่อนออกใบ
            </Button>
            {preview && previewKey !== rangeKey && (
              <Typography className="sub">ช่วงวันที่เปลี่ยน — ตรวจใหม่อีกครั้ง</Typography>
            )}
          </Stack>
          {rangeProblem && <Alert severity="error">{rangeProblem}</Alert>}
          {busy === "preview" && <LinearProgress />}
          {failure && (
            <Alert severity="error" onClose={() => setFailure(null)} data-testid="bdc-billing-error">
              {failure.message}
            </Alert>
          )}
          {issued && (
            <Alert severity="success" data-testid="bdc-billing-issued">
              <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                <span>
                  ออกใบวางบิล <b className="mono">{issued.billing_no}</b> แล้ว · {pcs(issued.invoice_count)} INV ·{" "}
                  {pcs(issued.credit_note_count)} CN · สุทธิ ฿{baht(issued.grand_total)}
                </span>
                <DownloadButtons billing={issued} busy={downloading} onDownload={download} />
              </Stack>
            </Alert>
          )}

          {current && (
            <Stack gap={1.5} data-testid="bdc-billing-preview">
              <Stack direction="row" gap={3} flexWrap="wrap" sx={{ bgcolor: "#F5F5F6", borderRadius: "8px", p: 1.5 }}>
                <Stat label="เลขที่ (ถ้าออกตอนนี้)" value={current.suggested_billing_no} />
                <Stat label="เดือน" value={thaiMonth(current.period_month)} />
                <Stat label="INV" value={`${pcs(current.invoice_count)} ใบ`} />
                <Stat label="CN" value={`${pcs(current.credit_note_count)} ใบ`} />
                <Stat label="มูลค่าสินค้า" value={baht(current.totals.subtotal)} />
                <Stat label="VAT" value={baht(current.totals.vat_amount)} />
                <Stat label="ยอดเงินสุทธิ" value={`฿${baht(current.totals.grand_total)}`} strong />
              </Stack>

              <BillingCheckList checks={current.checks} />

              <Box>
                <Typography fontWeight={600} mb={0.75}>
                  แถวในใบวางบิล ({pcs(current.row_count ?? current.rows.length)} แถว)
                </Typography>
                {truncated && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    แสดง {pcs(current.rows.length)} แถวแรก — ไฟล์ Excel / PDF ที่ออกมีครบทุกแถว
                  </Alert>
                )}
                <BillingRowsTable rows={current.rows} totals={current.totals} testId="bdc-billing-preview-rows" />
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="flex-start">
                  <DatePicker
                    label="วันที่เอกสาร"
                    value={dayjs(docDate)}
                    format="DD/MM/YYYY"
                    onChange={(value) => value && value.isValid() && setDocDate(value.format("YYYY-MM-DD"))}
                    slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
                  />
                  <DatePicker
                    label="วันครบกำหนด"
                    value={dayjs(dueDate)}
                    format="DD/MM/YYYY"
                    onChange={(value) => value && value.isValid() && setDueDate(value.format("YYYY-MM-DD"))}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: 170 },
                        error: Boolean(dateProblem),
                        helperText: dateProblem ?? "ค่าเริ่ม: วันที่ 5 เดือนถัดไป เลี่ยงเสาร์-อาทิตย์",
                      },
                    }}
                  />
                  {current.credit_term && <Stat label="เครดิต" value={current.credit_term} />}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={checkCount.errors > 0 || Boolean(dateProblem) || busy !== null}
                    onClick={() => setConfirm(true)}
                  >
                    ออกใบวางบิล
                  </Button>
                </Stack>
              </LocalizationProvider>
              {checkCount.errors > 0 && (
                <Typography className="sub">มีข้อผิดพลาด {pcs(checkCount.errors)} รายการ — แก้ก่อนจึงออกใบได้</Typography>
              )}
            </Stack>
          )}
        </Card>

        <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={600}>รายการใบวางบิล</Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              size="small"
              label="ปี"
              value={year}
              onChange={(e) => {
                setYear(Number(e.target.value));
                setPage(0);
              }}
              sx={{ width: 110 }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y + 543}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="สถานะ"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              sx={{ width: 130 }}
            >
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              <MenuItem value="issued">ออกแล้ว</MenuItem>
              <MenuItem value="cancelled">ยกเลิก</MenuItem>
            </TextField>
          </Stack>
          {listLoading && <LinearProgress />}
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t" data-testid="bdc-billing-list">
              <thead>
                <tr>
                  <th>เลขที่</th>
                  <th>เดือน</th>
                  <th>ช่วง INV</th>
                  <th>วันที่เอกสาร</th>
                  <th>ครบกำหนด</th>
                  <th className="n">INV</th>
                  <th className="n">CN</th>
                  <th className="n">สุทธิ</th>
                  <th>สถานะ</th>
                  <th>ออกโดย</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.uuid}>
                    <td className="mono">
                      <b>{row.billing_no}</b>
                    </td>
                    <td>{thaiMonth(row.period_month)}</td>
                    <td>
                      {thaiDate(row.date_from)} – {thaiDate(row.date_to)}
                    </td>
                    <td>{thaiDate(row.doc_date)}</td>
                    <td>{thaiDate(row.due_date)}</td>
                    <td className="n">{pcs(row.invoice_count)}</td>
                    <td className="n">{pcs(row.credit_note_count)}</td>
                    <td className="n">
                      <b>{baht(row.grand_total)}</b>
                    </td>
                    <td>
                      {row.status === "cancelled" ? <Tag tone="crit">ยกเลิก</Tag> : <Tag tone="ok">ออกแล้ว</Tag>}
                      {row.status === "cancelled" && row.cancel_reason && <div className="sub">{row.cancel_reason}</div>}
                    </td>
                    <td className="sub">
                      {row.created_by_name ?? ""} {thaiDateTime(row.created_at)}
                    </td>
                    <td>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        <Button size="small" variant="outlined" loading={viewLoading === row.uuid} onClick={() => openDetail(row)}>
                          ดู
                        </Button>
                        <DownloadButtons billing={row} busy={downloading} onDownload={download} />
                        {row.status === "issued" && (
                          <Button size="small" variant="outlined" color="error" onClick={() => setCancelling(row)}>
                            ยกเลิก
                          </Button>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && !listLoading && (
                  <tr>
                    <td colSpan={11} className="sub" style={{ textAlign: "center", padding: 16 }}>
                      ยังไม่มีใบวางบิลในปีนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          {listCount > LIST_LIMIT && (
            <TablePagination
              component="div"
              count={listCount}
              page={page}
              rowsPerPage={LIST_LIMIT}
              rowsPerPageOptions={[LIST_LIMIT]}
              onPageChange={(_e, next) => setPage(next)}
            />
          )}
        </Card>
      </Stack>

      <Dialog open={confirm} onClose={busy ? undefined : () => setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>ออกใบวางบิล</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {current && (
              <>
                เลขที่ <b className="mono">{current.suggested_billing_no}</b> (ระบบยืนยันเลขตอนออก) · {thaiMonth(current.period_month)}
                <br />
                {pcs(current.invoice_count)} INV · {pcs(current.credit_note_count)} CN · สุทธิ <b>฿{baht(current.totals.grand_total)}</b>
                <br />
                วันที่เอกสาร {thaiDate(docDate)} · ครบกำหนด {thaiDate(dueDate)}
                {checkCount.warnings > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    มีคำเตือน {pcs(checkCount.warnings)} รายการ — ตรวจแล้วจึงยืนยัน
                  </Alert>
                )}
                <Typography variant="caption" component="div" mt={1}>
                  INV / CN ในใบนี้จะอยู่ในใบวางบิลอื่นไม่ได้ และ CN ในใบยกเลิกไม่ได้ จนกว่าจะยกเลิกใบวางบิล
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={busy === "create"}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="warning" loading={busy === "create"} onClick={create}>
            ยืนยันออกใบ
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewing !== null} onClose={() => setViewing(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          ใบวางบิล {viewing?.billing_no} · {viewing ? thaiMonth(viewing.period_month) : ""}
        </DialogTitle>
        <DialogContent sx={TABLE_SX}>
          {viewing && (
            <Stack gap={1.5}>
              <Stack direction="row" gap={3} flexWrap="wrap">
                <Stat label="วันที่เอกสาร" value={thaiDate(viewing.doc_date)} />
                <Stat label="ครบกำหนด" value={thaiDate(viewing.due_date)} />
                {viewing.credit_term && <Stat label="เครดิต" value={viewing.credit_term} />}
                <Stat label="INV / CN" value={`${pcs(viewing.invoice_count)} / ${pcs(viewing.credit_note_count)}`} />
                <Stat label="ยอดเงินสุทธิ" value={`฿${baht(viewing.grand_total)}`} strong />
              </Stack>
              {viewing.status === "cancelled" && (
                <Alert severity="error">
                  ยกเลิกแล้ว: {viewing.cancel_reason} · {viewing.cancelled_by_name ?? ""} {thaiDateTime(viewing.cancelled_at)}
                </Alert>
              )}
              <BillingRowsTable
                rows={viewing.rows ?? []}
                totals={{ subtotal: viewing.subtotal, vat_amount: viewing.vat_amount, grand_total: viewing.grand_total }}
                testId="bdc-billing-detail-rows"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewing && <DownloadButtons billing={viewing} busy={downloading} onDownload={download} />}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setViewing(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <ReasonDialog
        open={cancelling !== null}
        title={`ยกเลิกใบวางบิล ${cancelling?.billing_no ?? ""}`}
        warning={
          <>
            เลข {cancelling?.billing_no} จะถูกบันทึกเป็น "ยกเลิก" · INV / CN ทั้งหมดในใบจะว่าง นำไปออกใบวางบิลใหม่ได้
            (ได้เลขใหม่)
          </>
        }
        confirmLabel="ยืนยันยกเลิกใบวางบิล"
        onClose={() => setCancelling(null)}
        onConfirm={cancel}
      />
    </Box>
  );
}
